import ServerCodeModel from './ServerCodeModel.js';
import { isOnline } from '../utils/network';
import { getSubjectData, setSubjectData } from '../utils/storage';
import ACTApiService from '../services/ACTApiService.js';

class TaskModel {
    constructor(logType, storageKey) {
        this.logType = logType;
        this.storageKey = storageKey;
        this.data = {};
        this.serverCodeModel = new ServerCodeModel();
        this.serverCodeModel.logType = logType;
        this.actApiService = new ACTApiService();
    }
    
    async fetchData() {
        const subjectId = localStorage.getItem('currentSubjectId') || localStorage.getItem('subjectId');
        const subjectData = getSubjectData(subjectId);
        
        // Always prioritize local data if it exists and has recent modifications
        if (subjectData[this.storageKey]) {
            const localData = subjectData[this.storageKey];
            
            // If we have local data with recent modifications, use it
            if (localData.timeLastModified) {
                this.data = localData;
                console.log('Using local data with recent modifications:', this.data);
                
                // Ensure entrySummary exists for verbal fluency tasks
                this.ensureEntrySummary();
                return;
            }
        }
        
        // Fallback to offline check
        if (!isOnline() && subjectData[this.storageKey]) {
            this.data = subjectData[this.storageKey];
            console.log('Using local data (offline):', this.data);
            
            // Ensure entrySummary exists for verbal fluency tasks
            this.ensureEntrySummary();
            return;
        }

        // Get existing data first
        this.data = subjectData[this.storageKey] || {};

        console.log('Fetched data:', this.data);
    }
    
    // Method to merge local and server data intelligently
    mergeData(localData, serverData) {
        if (!localData) return serverData;
        if (!serverData) return localData;
        
        const localTime = new Date(localData.timeLastModified || 0);
        const serverTime = new Date(serverData.timeLastModified || 0);
        
        // If local data is more recent, use it as base and merge non-critical server fields
        if (localTime > serverTime) {
            console.log('Local data is more recent, using it as base');
            return {
                ...serverData,
                ...localData,
                // Preserve server metadata but keep local user data
                timeCreated: serverData.timeCreated || localData.timeCreated,
                timeLastModified: localData.timeLastModified
            };
        } else {
            // Server data is more recent, use it as base and merge local user data
            console.log('Server data is more recent, using it as base');
            return {
                ...localData,
                ...serverData,
                // Preserve local user data if it exists
                entries: localData.entries || serverData.entries,
                administeringNotes: localData.administeringNotes || serverData.administeringNotes,
                scoringNotes: localData.scoringNotes || serverData.scoringNotes,
                entrySummary: localData.entrySummary || serverData.entrySummary
            };
        }
    }

    // Helper method to ensure entrySummary exists
    ensureEntrySummary() {
        // For verbal fluency tasks, ensure entrySummary exists
        if ((this.storageKey === 'verbalFluencyLetterFData' || this.storageKey === 'letterFData') && 
            !this.data.entrySummary) {
            this.data.entrySummary = { c: 0, i: 0, r: 0, q: 0, ir: 0 };
        }
        
        if ((this.storageKey === 'verbalFluencySupermarketData' || this.storageKey === 'supermarketData') && 
            !this.data.entrySummary) {
            this.data.entrySummary = { c: 0, i: 0, r: 0, q: 0, ir: 0 };
        }
        
        // For verbal fluency tasks, ensure entries array exists
        if ((this.storageKey === 'verbalFluencyLetterFData' || this.storageKey === 'letterFData' || 
            this.storageKey === 'verbalFluencySupermarketData' || this.storageKey === 'supermarketData') && 
            !this.data.entries) {
            this.data.entries = Array.from({ length: 60 }, (_, i) => ({
                entryId: i + 1,
                enteredWord: '',
                selectedAnswer: 'c'
            }));
        }
    }

    async saveData(data) {
        console.log('Saving data:', data);
        const subjectId = localStorage.getItem('currentSubjectId') || localStorage.getItem('subjectId');
        
        if (!subjectId) {
            console.error('No subject ID found');
            return;
        }
        
        // Update the timestamps
        if (!data.timeCreated) {
            data.timeCreated = new Date().toUTCString();
        }
        data.timeLastModified = new Date().toUTCString();
        
        // Ensure entrySummary exists for verbal fluency tasks
        if (this.storageKey.includes('verbalFluency') && data.entries) {
            data.entrySummary = this.calculateEntrySummary(data);
        }
        
        // Make sure the audioUrl is properly stored
        if (data.audioUrl) {
            console.log('Saving data with audio URL:', data.audioUrl);
        }
        
        setSubjectData(subjectId, this.storageKey, data);
        this.data = data;
        
        console.log(`Saved ${this.storageKey} data:`, data);
    }

    // Helper method to calculate entrySummary
    calculateEntrySummary(data) {
        const summary = { c: 0, i: 0, r: 0, q: 0, ir: 0 };
        
        if (data && data.entries) {
            data.entries.forEach(entry => {
                if (entry.selectedAnswer) {
                    const key = entry.selectedAnswer.toLowerCase();
                    if (Object.prototype.hasOwnProperty.call(summary, key)) {
                        summary[key]++;
                    }
                }
            });
        }
        
        console.log('Calculated summary:', summary);
        return summary;
    }

    async submitData(data) {
        console.log('Submitting data:', data);
        const uniqueData = JSON.parse(JSON.stringify(data));
        uniqueData.logType = this.logType;

        if (isOnline()) {
            console.log('Using logType:', this.logType);
            await this.serverCodeModel.saveData(uniqueData);
            if (this.serverCodeModel.saveResult.ok) {
                console.log('Data saved successfully on server');
                
                await this.saveData(uniqueData);
                await this.syncVisitData();
                
                return;
            }
            console.error('Failed to save data:', this.serverCodeModel.saveResult.error);
        }
        
        await this.saveData(uniqueData);
        console.log('Data saved locally');
        await this.syncVisitData();
    }
    
    async syncVisitData() {
        try {
            const participantId = localStorage.getItem('currentParticipantId');
            const visitTypeId = localStorage.getItem('visitTypeId');
            const lGroupId = localStorage.getItem('actGroupId');
            const serverCode = localStorage.getItem('subjectId');
            
            if (!participantId || !visitTypeId || !lGroupId || !serverCode) {
                console.warn('Missing required IDs for visit data sync', {
                    participantId,
                    visitTypeId,
                    lGroupId,
                    serverCode
                });
                return;
            }
            
            console.log(`Syncing visit data for ${this.storageKey} task`);
            
            const subjectData = JSON.parse(localStorage.getItem('subjectData') || '{}');
            const participantData = subjectData[serverCode] || {};
            
            if (!participantData[this.storageKey]) {
                console.warn(`Data for ${this.storageKey} not found in localStorage for ${serverCode}`);
            } else {
                console.log(`Found data for ${this.storageKey} in localStorage:`, {
                    timeLastModified: participantData[this.storageKey].timeLastModified
                });
            }
            
            await this.actApiService.updateVisitData(lGroupId, participantId, visitTypeId, serverCode);
            console.log('Visit data synchronized successfully');
        } catch (error) {
            console.error('Failed to sync visit data:', error);
        }
    }
}

export default TaskModel;